import type {AriaAttributes, AriaRole, MouseEvent} from 'react';
import React from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import Link from 'next/link';

import {
    getButtonBackgroundColor,
    getButtonBackgroundHoverColor,
    getButtonFontColor
} from './utils';
import {transient} from 'Application/configs/emotionTransientProps';
import type {IconType} from 'Images/icons';

import type {ButtonVariant, ButtonWidth} from './utils';
import type {WithOptionalOnlyTextChild} from 'types/global';

type LinkRel = 'nofollow noindex' | 'nofollow' | 'noindex' | 'noopener nofollow noindex' | 'noopener nofollow'
    | 'noopener noindex' | 'noopener noreferrer nofollow noindex' | 'noopener noreferrer nofollow'
    | 'noopener noreferrer noindex' | 'noopener noreferrer' | 'noopener' | 'noreferrer nofollow noindex'
    | 'noreferrer nofollow' | 'noreferrer noindex' | 'noreferrer';

/**
 * Defines the base properties for a button or link component. This interface supports conditional types based on the component type.
 * It provides a flexible way to define properties common to both buttons and links, as well as properties specific to each.
 * This approach simplifies the creation of a component that can render either a button or a link based on the provided props.
 */
interface BaseProps<C extends 'button' | 'link'> {
    /**
     * Specifies the element type to render: 'button' for a `<button>` element or 'link' for an `<Link>` element.
     * - `'button'`: Render a button element.
     * - `'link'`: Render a next link element.
     *
     * @default 'button'
     */
    as: C;
    /**
     * Optional CSS class name to customize the appearance of the button or link.
     */
    className?: string;
    forceSmall?: boolean;
    /**
     * An optional icon element to display alongside the button text.
     */
    icon?: IconType;
    /**
     * Determines the position of the icon relative to the button text.
     * - `'start'`: The icon will appear before the text.
     * - `'end'`: The icon will appear after the text.
     */
    iconPosition?: 'end' | 'start';
    /**
     * If the component is disabled, it will not be clickable and will have a different visual appearance.
     */
    isDisabled?: boolean;
    role?: AriaRole;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * It is a required property and must be provided when an object of type `ComponentProps` is expected.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     */
    testId?: string;
    /**
     * Indicates the button's variant, affecting its visual depth or emphasis.
     * - `'primary'`: A button with a primary color and high emphasis.
     * - `'secondary'`: A button with a secondary color and medium emphasis.
     * - `'outlined'`: A button with a border and transparent background.
     *
     * @default 'primary'
     */
    variant?: ButtonVariant;
    /**
     * Sets the width of the button or link, allowing for fixed or fluid sizing.
     * - `'auto'`: The button or link will expand to fit its content.
     * - `'full'`: The button or link will expand to fill its container.
     *
     * @default 'auto'
     */
    width?: ButtonWidth;
}

/**
 * Extends `BaseProps` to include additional properties specific to either a button or a link. This type uses conditional types
 * based on the value of `as` to include or exclude properties accordingly, ensuring type safety and relevance of props based on the element type.
 */
type ButtonProps<C extends 'button' | 'link'> = BaseProps<C> & (C extends 'button' ? {
    formId?: string;
    /**
     * Do not use as it is not applicable to buttons.
     */
    href?: never;
    /**
     * Optional click event handler.
     */
    onClick?(e: MouseEvent<HTMLButtonElement>): void;
    /**
     * Do not use as it is not applicable to buttons.
     */
    rel?: never;
    /**
     * Specifies the tab order of the link when navigating through the page.
     * A negative value (e.g., `-1`) will remove the link from the tab order, making it non-focusable.
     * If not specified, the link will follow the default tab order.
     */
    tabIndex?: number;
    /**
     * Do not use as it is not applicable to buttons.
     */
    target?: never;
    /**
     * Defines the button type.
     * - `'button'`: An normal button.
     * - `'reset'`: An reset button for forms.
     * - `'submit'`: An submit button for forms.
     *
     * @default 'button'
     */
    type?: 'button' | 'reset' | 'submit';
} : C extends 'link' ? {
    formId?: never;
    /**
     * URL for the link element to link to.
     */
    href: string;
    /**
     * Optional click event handler.
     */
    onClick?(e: MouseEvent<HTMLAnchorElement>): void;
    /**
     * Defines the relationship between the current document and the linked URL.
     * - `'nofollow'`: Indicates that the link is not endorsed by the original document's author.
     * - `'noindex'`: Indicates that the linked document should not be indexed by search engines.
     * - `'noopener'`: Ensures that the linked document does not have access to the originating document.
     * - `'noreferrer'`: Ensures that the linked document does not have access to the originating document and does not pass referrer information.
     * Also every combination of these values is possible.
     */
    rel?: LinkRel;
    /**
     * Specifies the tab order of the link when navigating through the page.
     * A negative value (e.g., `-1`) will remove the link from the tab order, making it non-focusable.
     * If not specified, the link will follow the default tab order.
     */
    tabIndex?: number;
    /**
     * Specifies where to open the linked URL.
     * - `'_blank'`: Opens the linked document in a new window or tab.
     * - `'_parent'`: Opens the linked document in the parent frame.
     * - `'_self'`: Opens the linked document in the same frame as it was clicked.
     * - `'_top'`: Opens the linked document in the full body of the window.
     */
    target?: '_blank' | '_parent' | '_self' | '_top';
    /**
     * Do not use as it is not applicable to links.
     */
    type?: never;
} : never);

/**
 * A versatile button component that supports both `<button>` and `<a>` (link) elements through a single unified interface.
 * It can be used in various parts of an application where user interaction is required. The component is highly customizable
 * with support for different sizes, styles, and variants. It also allows for additional HTML attributes specific to buttons and links.
 *
 * @param props              The component props.
 * @param props.as           Determines the component's base element type: 'button' for `<button>` or 'link' for `<a>`.
 * @param props.children     The content to be displayed within the button or link.
 * @param props.className    Optional CSS class name to add to the button or link element.
 * @param props.forceSmall   If `true`, forces the button to use small padding and font size, regardless of content.
 * @param props.formId       The ID of the form to associate with the button. Only applicable when `as='button'`.
 * @param props.href         URL for the link element. This prop should only be used when `as='link'`.
 * @param props.icon         An optional icon element to display alongside the button text.
 * @param props.iconPosition Determines the position of the icon relative to the button text.
 * @param props.isDisabled   If `true`, the button or link will be disabled and not clickable.
 * @param props.onClick      Optional click event handler. Applies to both button and link variants.
 * @param props.rel          Specifies the relationship between the current document and the linked document. Only applicable when `as='link'`.
 * @param props.size         Specifies the size of the button, affecting its padding and font size.
 * @param props.style        The visual style of the button or link (e.g., primary, secondary).
 * @param props.tabIndex     Specifies the tab order of the button or link when navigating through the page.
 * @param props.target       Specifies where to open the linked document. Only applicable when `as='link'`.
 * @param props.testId       A unique identifier for testing purposes.
 * @param props.type         Specifies the button type (`'button'`, `'reset'`, or `'submit'`). Only applicable when `as='button'`.
 * @param props.variant      The variant of the button, affecting its visual presentation.
 * @param props.width        Specifies the width of the button, allowing for fluid or fixed width.
 * @param props.role         The ARIA role attribute, defining the role of the button or link for accessibility purposes.
 * @returns A button or link element styled according to the provided props.
 *
 * @example
 * ```tsx
 * // Using Button as a link
 * <Button
 *   as="link"
 *   href="https://example.com"
 *   size="medium"
 *   style="primary"
 *   variant="contained"
 *   width="wide"
 *   testId="link-button-example"
 * >
 *   Visit Example
 * </Button>
 *
 * // Using Button as a button
 * <Button
 *   as="button"
 *   onClick={() => // Button clicked
 *   size="small"
 *   style="secondary"
 *   variant="outlined"
 *   width="auto"
 *   type="submit"
 *   testId="button-example"
 * >
 *   Submit
 * </Button>
 * ```
 */
const Button = <C extends 'button' | 'link'>({
    as,
    children,
    className,
    forceSmall = false,
    formId,
    href,
    icon,
    iconPosition = 'start',
    isDisabled = false,
    onClick,
    rel,
    role,
    tabIndex,
    target,
    testId = 'Button',
    type = 'button',
    variant = 'primary',
    width = 'auto',
    ...ariaProps
}: WithOptionalOnlyTextChild<ButtonProps<C>> & AriaAttributes
) => {
    // eslint-disable-next-line @nfq/component-single-hook
    const Icon = icon;

    return (as === 'link') ? (
        <LinkWrapper
            $iconPosition={iconPosition}
            $isSmall={!children || forceSmall}
            $variant={variant}
            $width={width}
            className={className}
            data-cy={testId}
            href={href!}
            rel={rel}
            role={role}
            tabIndex={tabIndex}
            target={target}
            onClick={onClick as (e: MouseEvent<HTMLAnchorElement>) => void}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...ariaProps}
        >
            {children}
            { }
            {(Icon) && (<Icon />)}
        </LinkWrapper>
    ) : (
        <ButtonWrapper
            $iconPosition={iconPosition}
            $isSmall={!children || forceSmall}
            $variant={variant}
            $width={width}
            className={className}
            data-cy={testId}
            disabled={isDisabled}
            form={formId}
            role={role}
            tabIndex={tabIndex}
            type={type}
            onClick={onClick as (e: MouseEvent<HTMLButtonElement>) => void}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...ariaProps}
        >
            {children}
            { }
            {(Icon) && (<Icon />)}
        </ButtonWrapper>
    );
};

Button.displayName = 'Button';

export {Button};

interface WrapperProps {
    $iconPosition?: 'end' | 'start';
    $isSmall?: boolean;
    $variant: ButtonVariant;
    $width: ButtonWidth;
}

const ButtonWrapper = styled('button', transient)<WrapperProps>`
    align-items: center;
    background-color: ${getButtonBackgroundColor};
    border: none;
    border-radius: 200px;
    color: ${getButtonFontColor};
    cursor: pointer;
    display: flex;
    flex-direction: ${({$iconPosition}) => ($iconPosition === 'start' ? 'row-reverse' : 'row')};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 1.4rem;
    font-weight: 500;
    gap: ${spacing(2.5)};
    justify-content: ${({$width}) => ($width === 'full' ? 'center' : null)};
    line-height: 1.4;
    padding: ${({$isSmall}) => ($isSmall ? `${spacing(2.5)} ${spacing(2.5)}` : `${spacing(3)} ${spacing(6)}`)};
    text-decoration: none;
    transition: color 0.3s ease-in-out, background-color 0.3s ease-in-out, outline 0.2s ease-in-out;
    width: ${({$width}) => ($width === 'full' ? '100%' : 'fit-content')};

    path[fill]:not([fill="transparent"]) {
        fill: ${getButtonFontColor};
    }

    path[stroke] {
        stroke: ${getButtonFontColor};
    }

    &:hover {
        background-color: ${getButtonBackgroundHoverColor};
    }

    &:active {
        background-color: ${getButtonBackgroundHoverColor};
        outline-color: ${({theme}) => theme.colors.focus};
        outline-offset: 4px;
        outline-style: solid;
        outline-width: 4px;
    }

    &:focus-visible {
        outline-color: ${({theme}) => theme.colors.focus};
        outline-offset: 4px;
        outline-style: solid;
        outline-width: 4px;
    }
`;

const LinkWrapper = ButtonWrapper.withComponent(Link);